import { useSyncExternalStore } from 'react';

/**
 * Cross-cutting auth signals that don't belong to the MSAL cache or the whoami
 * query but that pre-auth screens and the session-expired interstitial react to:
 *
 *  - `sessionExpired` — set by the axios token acquirer when a silent token
 *    renewal needs interaction (the refresh token is gone). The global
 *    `SessionExpiredHost` shows the re-auth interstitial; `WhoamiBoundary` uses
 *    it to tell a *renewal-failure* 401 (session expired) apart from a
 *    *valid-token* whoami 401 (a blocked account) — the two are the same status
 *    on the wire (see slice 02 goal + TD-191).
 *  - `signInFailed` — set by the bootstrap `handleRedirectPromise` catch when
 *    the MSAL interactive redirect cancelled/failed. `SignInPage` renders the
 *    "sign-in didn't complete" card (A3 case 4) instead of the landing.
 *
 * These live in a tiny external store (not React state / context) because they
 * are written from non-React code paths (the axios interceptor and the
 * pre-render bootstrap) and read from components. `useSyncExternalStore` keeps
 * subscribers in sync without a provider.
 */
export interface AuthStatus {
  sessionExpired: boolean;
  signInFailed: boolean;
}

let state: AuthStatus = { sessionExpired: false, signInFailed: false };
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function set(patch: Partial<AuthStatus>): void {
  const next: AuthStatus = { ...state, ...patch };
  if (next.sessionExpired === state.sessionExpired && next.signInFailed === state.signInFailed) {
    return;
  }
  state = next;
  emit();
}

/** Flag a silent-renewal failure so the re-auth interstitial appears. */
export function markSessionExpired(): void {
  set({ sessionExpired: true });
}

/** Clear the session-expired flag (on re-auth). */
export function clearSessionExpired(): void {
  set({ sessionExpired: false });
}

/** Flag that the MSAL interactive sign-in redirect did not complete. */
export function markSignInFailed(): void {
  set({ signInFailed: true });
}

/** Imperative snapshot for non-React callers and tests. */
export function getAuthStatus(): AuthStatus {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): AuthStatus {
  return state;
}

/** Subscribe a component to the auth-signal store. */
export function useAuthStatus(): AuthStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * The "you've been signed out" hint is set just before the MSAL logout redirect
 * and must survive the round-trip through Entra's hosted logout page. It lives
 * in `sessionStorage` (per-tab, preserved across cross-origin navigations) — a
 * module flag would be lost when the page reloads on return.
 *
 * `peekSignedOut` is a pure read (safe in a `useState` initializer, including
 * under StrictMode double-invocation); `clearSignedOut` is idempotent and is
 * called from an effect once the landing has captured the flag.
 */
const SIGNED_OUT_KEY = 'ami.signedOut';

export function markSignedOut(): void {
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, '1');
  } catch {
    // Private-mode / storage-disabled: the banner is a nicety, not load-bearing.
  }
}

export function peekSignedOut(): boolean {
  try {
    return sessionStorage.getItem(SIGNED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearSignedOut(): void {
  try {
    sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // No-op — see markSignedOut.
  }
}

/**
 * "`RequireAuth` has already sent this tab to Microsoft once." Like the
 * signed-out hint, this must survive a cross-origin round trip, so it lives in
 * `sessionStorage` rather than module state — and per-tab is exactly the right
 * scope, since MSAL's own token cache is `sessionStorage` too.
 *
 * It exists to bound the back button. If the user reaches the Microsoft page and
 * presses Back instead of signing in, they return to a guarded route with no
 * error hash and no session, which is indistinguishable from a first visit —
 * so the guard would redirect them straight back out, forever. The flag lets the
 * guard offer the "sign-in didn't complete" card with a working "Try again"
 * instead of bouncing a second time. It is cleared as soon as a session exists.
 */
const SIGN_IN_ATTEMPTED_KEY = 'ami.signInAttempted';

export function markSignInAttempted(): void {
  try {
    sessionStorage.setItem(SIGN_IN_ATTEMPTED_KEY, '1');
  } catch {
    // Private-mode / storage-disabled: the guard falls back to redirecting,
    // which is the pre-existing behaviour rather than a new failure.
  }
}

export function peekSignInAttempted(): boolean {
  try {
    return sessionStorage.getItem(SIGN_IN_ATTEMPTED_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearSignInAttempted(): void {
  try {
    sessionStorage.removeItem(SIGN_IN_ATTEMPTED_KEY);
  } catch {
    // No-op — see markSignInAttempted.
  }
}

/** Test-only reset so store state doesn't leak across test cases. */
export function __resetAuthStatusForTests(): void {
  state = { sessionExpired: false, signInFailed: false };
  emit();
}
